import { isDefined } from '@evorait/pace-core';
import { Action, createReducer, on } from '@ngrx/store';
import moment from 'moment';
import { DocBoxListModel } from '../../services/docBox-odata.interface';
import {
    clearDocBoxData,
    downloadDocuments,
    downloadDocumentsLoadingStatus,
    filterDocuments,
    getDocBox,
    getDocBoxError,
    getDocBoxFromSideMenu,
    getDocBoxFromSideMenuSuccess,
    getDocBoxSuccess,
    selectAllDocuments,
    selectDocument,
    sortByField,
} from '../actions/docBox.action';
import { DocBoxState, initialDocBoxState } from '../state/docBox.state';
import { DocBoxReportStatus, DOCUMENT_FILTERS, DocumentState } from '../state/document.state';
import { handOverUnsubmittedFormsSuccess } from '../actions/state.actions';

function getDocBoxReducer(state: Readonly<DocBoxState>): Readonly<DocBoxState> {
    return {
        ...state,
        isLoading: true,
    };
}

function downloadDocumentsLoadingStatusReducer(
    state: Readonly<DocBoxState>,
    props: { docBoxId: string[] },
): Readonly<DocBoxState> {
    const files = state.docBoxFiles.map((file) => {
        let updatedFile = { ...file };
        props.docBoxId.map((id) => {
            if (id === file.docBoxId) {
                updatedFile = {
                    ...file,
                    isFileSelected: !file.isFileSelected,
                };
            }
        });

        return { ...updatedFile };
    });
    return {
        ...state,
        docBoxFiles: files,
        isAllFilesSelected: false,
        isLoading: false,
    };
}

function sortByFieldReducer(
    state: Readonly<DocBoxState>,
    props: { property: keyof DocumentState },
): Readonly<DocBoxState> {
    const files = [...state.docBoxFiles];

    const sortedField = {
        ...state.sortByField,
    };

    if (sortedField.field === props.property) {
        sortedField.ascending = !sortedField.ascending;
    } else {
        sortedField.field = props.property;
        sortedField.ascending = true;
    }

    files.sort((a, b) => {
        if (props.property === 'date') {
            const dateA = a[props.property] ? moment(a[props.property]) : null;
            const dateB = b[props.property] ? moment(b[props.property]) : null;

            if (dateA === null) {
                return 1;
            }
            if (dateB === null) {
                return -1;
            }

            return sortedField.ascending
                ? dateA.isBefore(dateB)
                    ? -1
                    : dateA.isAfter(dateB)
                      ? 1
                      : 0
                : dateA.isBefore(dateB)
                  ? 1
                  : dateA.isAfter(dateB)
                    ? -1
                    : 0;
        } else {
            return sortedField.ascending
                ? (a[props.property] ?? '') > (b[props.property] ?? '')
                    ? -1
                    : 0
                : (b[props.property] ?? '') > (a[props.property] ?? '')
                  ? -1
                  : 0;
        }
    });

    return {
        ...state,
        sortByField: sortedField,
        docBoxFiles: files,
    };
}

function getDocBoxSuccessReducer(
    state: Readonly<DocBoxState>,
    props: { docBoxData: readonly DocBoxListModel[] },
): Readonly<DocBoxState> {
    const files = props.docBoxData.map((file) => {
        const dateTimeStamp = file.Date?.replace(/\/Date\((\d+)\)\//, '$1');
        const dateString = dateTimeStamp
            ? dateTimeStamp.length > 0
                ? moment(parseInt(dateTimeStamp, 10))
                : moment.invalid()
            : null;

        return {
            docBoxId: file.DocBoxId,
            docType: file.DocType,
            reportType: file.ReportType,
            title: file.Title,
            printNumber: file.PrintNumber,
            date: dateString ? dateString.format('YYYY-MM-DD') : null,
            createdBy: file.CreatedBy,
            orderNumber: file.OrderNumber,
            reportStatus:
                file.ReportStatus === 'INTERIM'
                    ? DocBoxReportStatus.INTERIM
                    : file.ReportStatus === 'FINAL'
                      ? DocBoxReportStatus.FINAL
                      : undefined,
            fileType: file.FileType,
            isFileSelected: false,
            isFileVisible: true,
            funcLoc: file.Funcloc,
            serialNo: file.SerialNo,
            material: file.Material,
            equipment: file.EquipmentNumber,
            notification: file.Notification,
            notificationType: file.NotificationType,
            language: file.Language,
            description: file.Description,
        };
    });
    const newState: DocBoxState = {
        ...state,
        docBoxFiles: files,
        isAllFilesSelected: false,
        isLoading: false,
    };

    return sortByFieldReducer(newState, { property: 'date' });
}

function getDocBoxErrorReducer(state: Readonly<DocBoxState>): Readonly<DocBoxState> {
    return {
        ...state,
        docBoxFiles: [],
        isAllFilesSelected: false,
        isLoading: false,
    };
}

function selectDocumentReducer(state: Readonly<DocBoxState>, props: { docBoxId: string }): Readonly<DocBoxState> {
    const files = state.docBoxFiles.map((file) => {
        let updatedFile = { ...file };
        if (props.docBoxId === file.docBoxId) {
            updatedFile = {
                ...file,
                isFileSelected: !file.isFileSelected,
            };
        }
        return { ...updatedFile };
    });
    const isAllFilesSelected = files.every((file) => file.isFileSelected);
    return {
        ...state,
        docBoxFiles: files,
        isAllFilesSelected,
    };
}

function selectAllDocumentsReducer(state: Readonly<DocBoxState>): Readonly<DocBoxState> {
    const files = state.docBoxFiles.map((file) => {
        return {
            ...file,
            isFileSelected: !state.isAllFilesSelected,
        };
    });
    return {
        ...state,
        docBoxFiles: files,
        isAllFilesSelected: !state.isAllFilesSelected,
    };
}

function filterDocumentsReducer(
    state: Readonly<DocBoxState>,
    props: { property: DOCUMENT_FILTERS; value: string },
): Readonly<DocBoxState> {
    const filters = { ...state.filters };
    filters[props.property] = props.value;
    const activeFilterKeys = Object.keys(filters).filter((key: DOCUMENT_FILTERS) => filters[key].length > 0);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore --- @TEO can you please check that?
    const files: Array<Readonly<DocumentState>> = state.docBoxFiles.map((file) => {
        const isFileVisible = activeFilterKeys.reduce((isVisible, key: DOCUMENT_FILTERS) => {
            return isDefined(file[key]) && file[key]?.toLowerCase().includes(filters[key].toLowerCase()) && isVisible;
        }, true);
        return {
            ...file,
            isFileVisible,
        };
    });
    return {
        ...state,
        docBoxFiles: files,
        filters,
    };
}

const createDocBoxReducer = createReducer(
    initialDocBoxState,
    on(getDocBoxSuccess, getDocBoxSuccessReducer),
    on(getDocBoxFromSideMenuSuccess, getDocBoxSuccessReducer),
    on(getDocBoxError, getDocBoxErrorReducer),
    on(getDocBox, getDocBoxReducer),
    on(getDocBoxFromSideMenu, getDocBoxReducer),
    on(selectDocument, selectDocumentReducer),
    on(selectAllDocuments, selectAllDocumentsReducer),
    on(filterDocuments, filterDocumentsReducer),
    on(sortByField, sortByFieldReducer),
    on(downloadDocuments, getDocBoxReducer),
    on(downloadDocumentsLoadingStatus, downloadDocumentsLoadingStatusReducer),
    on(clearDocBoxData, () => initialDocBoxState),
    on(handOverUnsubmittedFormsSuccess, () => initialDocBoxState),
);

export function docBoxReducer(state: Readonly<DocBoxState>, action: Action): Readonly<DocBoxState> {
    return createDocBoxReducer(state, action);
}
